import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Typography, Steps, Button, Select, Radio, Switch,
  Input, Row, Col, Tag, Divider, Space, Spin, Alert,
  message, Tooltip, Upload, Checkbox, Modal, ColorPicker,
  Popover, InputNumber, Popconfirm,
} from 'antd';
import {
  FilePdfOutlined, DownloadOutlined, ArrowUpOutlined,
  ArrowDownOutlined, DeleteOutlined, PlusOutlined,
  FileImageOutlined, FileTextOutlined, TeamOutlined,
  LoadingOutlined, UploadOutlined, SaveOutlined,
  PrinterOutlined, GlobalOutlined, EditOutlined,
  TableOutlined, IdcardOutlined, AppstoreOutlined, BookOutlined,
} from '@ant-design/icons';
import { eventoService, pdfService, fixUrl } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';
import {
  GENEALOGIA_VISTA_INFO, DEPS_VISTA_INFO, GENEALOGIA_VISTA_DEFAULT, DEPS_VISTA_DEFAULT, vistasDeConfig,
} from '../../utils/vistasFicha';
import RichTextEditor from '../../components/RichTextEditor';

const { Title, Text } = Typography;
const { Option } = Select;

// Genera un ID único para cada sección en el estado local
const uid = () => Math.random().toString(36).slice(2, 9);

// Las DOS vistas del paso «Contenido» del catálogo (2026-10-27). Por
// categoría y raza es la de siempre: una sección por combinación, con su
// portada adentro. Por corral es una sección por CORRAL, para mover cada uno
// a mano; ahí la portada de sección es un ítem suelto («Agregar portada»),
// porque una portada por corral serían decenas de hojas. Sólo el catálogo
// (impresión y digital) la lee: tablas y carátulas no usan las secciones.
const VISTA_CONTENIDO_INFO = {
  categoria: { label: 'Por categoría y raza', desc: 'Una sección por categoría y raza, con su portada. Los animales van en orden de corral adentro de cada una.' },
  corral: { label: 'Por corral', desc: 'Una sección por corral, para ordenarlos uno por uno. Las portadas y las publicidades se agregan sueltas y van donde las pongas.' },
};
// Los grupos de a dos se guardan por sección: la clave es la combinación en
// una vista y el corral en la otra. UNA función, para que el ítem y el envío
// al backend no la armen cada uno por su lado.
const claveGrupos = (sec) => (
  sec.tipo === 'corral' ? `corral_${sec.corral}` : `${sec.id_categoria}_${sec.id_raza}`
);
const tieneAnimales = (sec) => sec.tipo === 'grupo' || sec.tipo === 'corral';

// Fecha y hora cortas para los carteles de la maquetación guardada.
const fechaCorta = (iso) => (iso
  ? new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');

// Las secciones de una maquetación guardada, sobre las FRESCAS del evento.
// Manda el orden guardado; de cada sección del evento (grupo, corral,
// condiciones) se toman los ajustes guardados —activa, título, imagen,
// textos— sobre los datos frescos (total de animales); una guardada que ya
// no existe en el evento (un corral que se vació) se descarta; las
// publicidades y portadas sueltas vuelven tal cual; y lo nuevo del evento
// que la maquetación no conocía se agrega al final.
const fusionarSecciones = (frescas, guardadas) => {
  const porId = new Map(frescas.map(s => [s.id, s]));
  const usadas = new Set();
  const resultado = [];
  for (const g of guardadas) {
    if (g.tipo === 'publicidad' || g.tipo === 'portada') {
      resultado.push({ ...g, _uid: uid() });
      continue;
    }
    const fresca = porId.get(g.id);
    if (!fresca) continue;
    usadas.add(g.id);
    resultado.push({ ...fresca, ...g, total_animales: fresca.total_animales, disponible: fresca.disponible, _uid: uid() });
  }
  for (const f of frescas) {
    if (!usadas.has(f.id)) resultado.push(f);
  }
  return resultado;
};

// Config por formato de salida. Fuente única: las tarjetas del paso 0 y la
// alerta del resumen se derivan de acá, así una descripción no puede quedar
// desincronizada del formato que describe.
const MODO_INFO = {
  // UNA card para el catálogo (2026-10-29, pedido del usuario): hasta ese día
  // «Digital» e «Impresión» eran dos formatos del paso 0, pero la maquetación
  // —orden, portadas, publicidades, cabecera, condiciones— es la MISMA y lo
  // único que cambia es la generación (fotos, color, anillado, links). Con la
  // maquetación guardada por (evento, modo), dos cards eran dos copias que se
  // desincronizaban. La salida se elige al final, con dos botones
  // (`SALIDA_INFO`); el backend sigue recibiendo `impresion` | `digital`.
  catalogo: {
    tag: 'purple', label: '📖 Catálogo', titulo: 'Catálogo', icono: BookOutlined,
    desc: 'El catálogo del remate: fotos, ficha, genealogía y DEP\'s. Una maquetación, que se genera para imprenta o digital.',
    alerta: 'La maquetación es la misma para las dos salidas: elegí abajo si la generás para imprenta o digital. Podés generar las dos, una después de la otra.',
    proximamente: false,
  },
  tablas: {
    tag: 'geekblue', label: '📊 Tablas', titulo: 'Tablas', icono: TableOutlined,
    desc: 'Planilla de datos por corral (genealogía, DEPs y enlaces a la web).',
    alerta: 'Planilla de datos por corral (genealogía, DEPs y enlaces a la web).',
  },
  caratulas: {
    tag: 'gold', label: '🪪 Carátulas', titulo: 'Carátulas', icono: IdcardOutlined,
    desc: 'Carteles para colgar en los corrales.',
    alerta: 'Carteles A4 apaisados para colgar en los corrales. Sin fotos: se generan rápido.',
  },
};

// Las dos SALIDAS del catálogo, los dos botones del último paso. Es lo que
// viaja como `modo` a `POST /pdf/generar`; `catalogo` nunca viaja ahí.
const SALIDA_INFO = {
  impresion: {
    titulo: 'Para imprenta', icono: PrinterOutlined, boton: 'Generar para imprenta',
    desc: 'CMYK, fotos en alta resolución y márgenes de anillado. Sin links: en papel no se tocan. Archivo pesado, puede demorar más.',
  },
  digital: {
    titulo: 'Digital', icono: GlobalOutlined, boton: 'Generar digital',
    desc: 'RGB y fotos livianas para pantalla, con el botón «Ver en línea» de cada animal. Para WhatsApp y email.',
  },
};

// Orden de la planilla de tablas: por categoría (el del catálogo) o por
// corral (como se camina el galpón el día del remate).
const ORDEN_TABLAS_INFO = {
  categoria: 'Agrupa por categoría y, dentro de cada una, una tarjeta por corral. Es el orden del catálogo.',
  corral:    'Una tarjeta por corral, en orden de corral, sin separar por categoría. Sirve para recorrer los corrales el día del remate.',
};

// Granularidad de las carátulas: una por corral, o siguiendo cómo se muestra
// cada animal en el evento (agrupado → por corral, individual → por animal).
const GRANULARIDAD_INFO = {
  evento: 'Respeta cómo se muestra cada animal en el evento: los corrales agrupados salen en una carátula por corral y los animales individuales, una cada uno.',
  corral: 'Una carátula por corral. Si el corral tiene un solo animal se usa la ficha individual completa.',
};

// Cabecera del cartel de corral o de la planilla de tablas: cinco ejes
// independientes. Forma, posición y ALTURA de la caja, QUÉ imagen va adentro (uno de
// los tres logos cargados en Micrositio → Estructura) y de qué COLOR
// es la caja (blanca, el primario o el secundario del micrositio, o uno suelto
// que viaja como hex). Los defaults son la cabecera histórica, y son lo que
// recibe el backend si el productor no toca nada en el paso.
// `externa` (2026-10-30): la URL de un diseño subido en el paso «Cabecera»
// que ocupa la banda entera en lugar de banda + título + caja del logo;
// vacía = la cabecera generada. `externa_titulo`: con la externa, si el nombre
// del remate va encima (la imagen es una TRAMA, el fondo) o no (la imagen es
// la CABECERA COMPLETA, con el nombre y el logo ya puestos por el diseñador).
const CABECERA_DEFAULT = { forma: 'capsula', posicion: 'izquierda', vertical: 'colgando', imagen: 'logo', fondo: 'blanco', externa: '', externa_titulo: true };
// Medidas que se le piden al diseñador para la cabecera externa: el ancho de
// la hoja (842pt) por el alto de la banda (48 el catálogo, 58 cartel y
// planilla), a 300 DPI. Un PDF de esa medida también sirve: el backend lo
// rasteriza a 300 DPI al subirlo.
const CM_POR_PT = 2.54 / 72;
const PX_POR_PT = 300 / 72;
const medidasCabeceraExterna = (bandaH) => ({
  px: `${Math.round(PREV_W * PX_POR_PT)} × ${Math.round(bandaH * PX_POR_PT)} px`,
  cm: `${(PREV_W * CM_POR_PT).toFixed(1)} × ${(bandaH * CM_POR_PT).toFixed(2)} cm`,
  proporcion: PREV_W / bandaH,
});
const FORMA_LABEL = { capsula: 'cápsula', circulo: 'círculo' };
// Quinto eje (2026-10-20, diseño de Facundo para OP1): a qué ALTURA va la caja.
// Tres presets, no un número: el productor elige un aspecto. Calcan
// `_alto_caja_logo` / `_y_caja_logo` de pdf_service_caratulas.py.
const VERTICAL_LABEL = { colgando: 'colgando de la banda', centrada: 'centrada en la banda', apoyada: 'apoyada en la línea' };
const VERTICALES_CABECERA = [
  { valor: 'colgando', label: 'Colgando', tip: 'La de siempre: la caja sobresale por debajo de la banda.' },
  { valor: 'centrada', label: 'Centrada', tip: 'Adentro de la banda, más chica. No cuelga nada.' },
  { valor: 'apoyada',  label: 'Apoyada',  tip: 'Apoyada sobre la línea fina, sin sobresalir.' },
];
const IMAGEN_LABEL = { logo: 'logo principal', mobile: 'logo mobile', iso: 'iso' };
// Las imágenes que puede llevar la caja, en el orden del radio. 'logo' es la
// columna `img_evento` y siempre se ofrece (sin él sale la forma vacía, como
// en el PDF); las otras dos se deshabilitan si el evento no las tiene cargadas.
const IMAGENES_CABECERA = [
  { valor: 'logo',   label: 'Principal' },
  { valor: 'mobile', label: 'Mobile' },
  { valor: 'iso',    label: 'Iso' },
];
const SIN_IMAGENES = { logo: '', mobile: '', iso: '' };
const FONDOS_CON_NOMBRE = ['blanco', 'primario', 'secundario'];
// Lo que arranca el picker cuando el productor elige "Otro": un color visible
// y neutro, que no se confunda con "no elegí nada".
const FONDO_OTRO_INICIAL = '#1a5f2a';
// Colores del micrositio cuando el evento todavía no los definió: los mismos
// defaults que usa el backend (COLOR_PRIMARY_DEFAULT / COLOR_SECONDARY_DEFAULT
// en pdf_service_impresion.py, de donde los importan carátulas y tablas). Si
// cambian allá, el preview mentiría acá.
// `termino`: "Corral" o "Lote" según `termino_lote` del micrositio, la misma
// palabra que el PDF escribe en el badge.
const COLORES_EVENTO_DEFAULT = { primario: '#1a5f2a', secundario: '#8b4513', termino: 'Corral' };
// Sin evento elegido no hay cabecera que imitar: el preview va en la paleta
// Puja (grafito y oro de frontend/src/index.css), como la herramienta misma
// (usuario, 2026-10-16). En cuanto elige un evento, pasa a los colores de ese
// micrositio; si el evento no los definió, a los defaults del PDF (arriba).
const COLORES_PUJA = { primario: '#232323', secundario: '#b4986d', termino: 'Corral' };

/** El hex con que se pinta la caja en el preview para un `fondo` dado. */
const colorFondoPreview = (fondo, colores) => {
  if (fondo === 'primario') return colores.primario;
  if (fondo === 'secundario') return colores.secundario;
  if (fondo === 'blanco') return '#ffffff';
  return fondo;   // ya es un hex
};

// Pasos del asistente. Los CUATRO formatos tienen "Cabecera" (todos dibujan
// con `_draw_header` de carátulas). "Estructura" lo tienen el catálogo
// (impresión y digital, la misma generación con otra salida: OP1 / OP2,
// genealogía y DEP's) y, desde el 2026-10-16, carátulas (sólo genealogía y
// DEP's, titulado "Ficha del cartel"). Tablas no. La navegación va POR
// ÍNDICE, así que esta lista tiene que ser la MISMA fuente para los Steps,
// para el contenido que se renderiza y para acotar el índice al cambiar de
// formato — tres listas paralelas se desincronizan en el primer cambio.
const PASOS_CON_CABECERA = ['evento', 'cabecera', 'contenido', 'generar'];
const PASOS_CATALOGO     = ['evento', 'estructura', 'cabecera', 'contenido', 'generar'];
const MODOS_CATALOGO     = ['catalogo'];
const MODOS_CON_ESTRUCTURA = [...MODOS_CATALOGO, 'caratulas'];
const tieneCabecera = () => true;
const tieneEstructura = (modo) => MODOS_CON_ESTRUCTURA.includes(modo);
// OP1 / OP2 es sólo del catálogo; carátulas no lo elige.
const eligeEstructura = (modo) => MODOS_CATALOGO.includes(modo);
const pasosDe = (modo) => (tieneEstructura(modo) ? PASOS_CATALOGO : PASOS_CON_CABECERA);
const TITULO_PASO = {
  evento: 'Evento', estructura: 'Estructura', cabecera: 'Cabecera', contenido: 'Contenido', generar: 'Generar',
};

// Las dos estructuras del catálogo (impresión y digital). OP1 es la de siempre
// (modelo del catálogo de Pilagá); OP2 calca el catálogo de muestra Bellamar:
// por animal, una hoja con la foto a sangre y la siguiente con la ficha.
const ESTRUCTURA_DEFAULT = 'op1';
const ESTRUCTURA_INFO = {
  op1: {
    label: 'OP1 · Clásica',
    desc: 'Una hoja por animal: foto a la izquierda, ficha a la derecha y genealogía abajo. Es el catálogo de siempre.',
  },
  op2: {
    label: 'OP2 · Foto a página completa',
    desc: 'Dos hojas por animal: la foto a página completa con el nombre y el premio, y la ficha completa en la hoja siguiente.',
  },
};
// Cómo se leen la genealogía y los DEP's: textos y claves compartidos con el
// panel del micrositio (`utils/vistasFicha.js`). Los radios ARRANCAN con lo
// que el productor eligió para el micrositio (`vistasDeConfig`) al cargar el
// evento, y acá puede cambiarlo para esta tirada.
//
// En el CATÁLOGO la misma clave 'arbol' se llama "Automático": el árbol de
// ramas sólo entra en la hoja B de OP2 y sin familiares con foto; en OP1 (y
// en OP2 con fotos) va la tabla compacta. Decirle "Árbol" prometería algo
// que en OP1 no pasa. "Árbol" es la clave 'nodos' (2026-10-27): el árbol de
// ramas SIEMPRE, y en OP1 la foto principal cede la altura que le falte.
const GENEALOGIA_CATALOGO_INFO = {
  arbol: { label: 'Automático', desc: 'Árbol de ramas cuando hay lugar (la hoja de ficha de OP2, sin familiares con foto); si no, la tabla compacta. En OP1 siempre la tabla.' },
  nodos: { label: 'Árbol', desc: 'El árbol de ramas siempre, en OP1 y en OP2. En OP1 la foto principal cede la altura que el árbol necesita; en OP2 va aunque haya familiares con foto, que bajan debajo si entran.' },
  tabla: { label: 'Tabla', desc: 'Siempre la tabla compacta, en OP1 y en OP2: la misma línea en todas las hojas del catálogo.' },
};

// Los tags del resumen final llevan frases enteras ("círculo · izquierda ·
// isotipo · fondo primario"): el Tag de antd no parte renglón y se pisaba con
// la columna de al lado (captura del usuario, 2026-10-09). Con esto el tag
// crece a lo alto en vez de desbordar a lo ancho.
const TAG_RESUMEN = { whiteSpace: 'normal', height: 'auto', lineHeight: '20px', paddingBlock: 2 };


// ─── Vista previa de la cabecera ─────────────────────────────────────────────
// Calca `_draw_header` de backend/app/services/pdf_service_caratulas.py EN LA
// MISMA ESCALA: el viewBox mide el ancho real de la hoja A4 apaisada en puntos,
// así que estos números son las mismas constantes del servicio. Si allá se mueve
// una medida, acá se mueve igual — es lo único que hace que el preview no mienta.
const PREV_W       = 842;    // ancho de A4 apaisado, en puntos
const PREV_H       = 110;    // la banda más lo que cuelga la caja del logo
const PREV_BANDA_H = 58;     // HEADER_H
const PREV_LINEA_H = 3;      // HEADER_LINE
const PREV_MARGEN  = 20;     // ML / MR / MARGEN_SUP
const PREV_BORDE   = 3;      // BORDE
// Lo que cuelga de la banda va a ESCALA 0.85 desde el 2026-10-09
// (`_ESCALA_CABECERA` en pdf_service_caratulas.py): caja y badge un 15% más
// chicos en todos los PDF.
const PREV_CAJA_H  = 71.1;   // _alto_badge_corral(): (HEADER_H + 30) * 0.95 * 0.85
const PREV_BADGE_W = 87;     // 102 * 0.85
const PREV_OFF     = 36;     // _CAJA_OFF: lo que la cápsula se sale de la hoja
const PREV_SANGRIA = 48;     // _CAJA_SANGRIA: ese recorte + el aire hasta el logo

// Ancho de la cápsula en el preview. En el PDF sale de la PROPORCIÓN del logo
// real (ver `_caja_logo`); acá se fija un ancho representativo porque lo que el
// productor tiene que entender es DÓNDE queda, no cuánto va a medir al punto.
const PREV_CAPSULA_W = 196;   // 230 * 0.85
// Los presets verticales: alto y Y (desde arriba) de la caja. Calcan
// `_alto_caja_logo` y `_y_caja_logo`: centrada = HEADER_H - 2·5 adentro de la
// banda; apoyada = arranca a 8 del filo y termina con la línea secundaria.
const PREV_CAJA_AIRE_BANDA   = 5;
const PREV_CAJA_AIRE_APOYADA = 8;
// `bandaH`: el alto de la banda. Carátulas y tablas llevan 58; el catálogo
// (impresión y digital) 48 desde el 2026-10-20 (`_HEADER_H_CATALOGO`), y lo
// que cuelga se achica con ella: `_alto_badge_corral(h)` = (h + 30) · 0.95 · 0.85.
const PREV_BANDA_H_CATALOGO = 48;
const PREV_TITULO_CATALOGO  = 15;
const cajaColgandoH = (bandaH) => (bandaH + 30) * 0.95 * 0.85;
const cajaVerticalPreview = (vertical, bandaH = PREV_BANDA_H) => {
  if (vertical === 'centrada') {
    const h = bandaH - PREV_CAJA_AIRE_BANDA * 2;
    return { y: (bandaH - h) / 2, h };
  }
  if (vertical === 'apoyada') {
    return { y: PREV_CAJA_AIRE_APOYADA, h: bandaH + PREV_LINEA_H - PREV_CAJA_AIRE_APOYADA };
  }
  return { y: PREV_MARGEN, h: cajaColgandoH(bandaH) };
};

// El preview se pinta con los COLORES DEL MICROSITIO del evento (banda =
// primario, línea y bordes = secundario, textos del badge en negro y título en
// blanco), que son los que usa `_draw_header`. Hasta el 2026-10-16 iba en la
// paleta Puja a propósito ("es un control, no una maqueta"); el usuario pidió
// lo contrario: que el productor vea la cabecera tal cual va a salir, porque
// justamente está eligiendo el color de la caja y la posición del logo.
// Los colores llegan por props (`colores`) desde la config del evento.
const PREV_NEGRO   = '#000000';
const PREV_GRIS    = '#bfbfbf';

// Recorta el título a su franja, igual que `_texto_cortado` en el PDF. Es un id
// fijo porque hay un solo preview en pantalla.
const PREV_CLIP_ID = 'prevZonaTituloCabecera';

// Ancho promedio de un carácter en bold, como fracción del cuerpo. En el PDF
// el recorte lo hace `stringWidth` con la métrica real de Helvetica; acá
// alcanza una estimación, porque el preview sólo tiene que mostrar SI el nombre
// entra en su franja, no componer la línea al punto.
const PREV_EM = 0.58;

/** Recorta con elipsis para que entre en `maxW`. Calca `_texto_cortado`. */
const textoCortadoPreview = (texto, maxW, size, spacing) => {
  const ancho = (t) => t.length * (size * PREV_EM + spacing);
  if (!texto || ancho(texto) <= maxW) return texto;
  let corte = texto;
  while (corte.length > 1 && ancho(`${corte}…`) > maxW) corte = corte.slice(0, -1);
  return `${corte}…`;
};

// Margen de anillado del catálogo de IMPRENTA (1,5 cm = 42,52pt, 2026-10-21):
// el logo y el badge quedan pasándolo; la cápsula que sangra se ensancha hacia
// adentro lo que haga falta (`_extra_sangria` en pdf_service_caratulas.py).
// "El color sangra, la información no". Sólo el modo impresión; null = sin
// anillado (digital, carátulas, tablas).
const PREV_MARGEN_ANILLADO = 1.5 * 72 / 2.54;
// Lo que BAJA la cabecera del catálogo anillado para que el tope del título
// quede a 1 cm (`_despeje_sup` en pdf_service_impresion.py): la banda crece
// eso hacia abajo y el título y la caja del logo bajan con ella. Banda de 48,
// título de 15, mayúscula 0,718 del cuerpo → ~9,9pt.
const PREV_DESPEJE_ANILLADO = Math.max(
  0, (72 / 2.54) - (PREV_BANDA_H_CATALOGO / 2 + 0.35 * PREV_TITULO_CATALOGO - 0.718 * PREV_TITULO_CATALOGO),
);
const extraSangriaPreview = (margen) => (
  margen ? Math.max(0, margen - (PREV_SANGRIA - PREV_OFF)) : 0
);

/**
 * Geometría de la caja del logo. Calca `_geometria_caja_logo`: la cápsula
 * SANGRA por el borde al que va pegada, el círculo nunca sangra, y el logo cae
 * siempre entero dentro de la hoja (y pasando el margen de anillado, si hay).
 */
const cajaLogoPreview = (forma, posicion, vertical = 'colgando', bandaH = PREV_BANDA_H, margen = null) => {
  const { y, h: altoCaja } = cajaVerticalPreview(vertical, bandaH);
  const pad = PREV_BORDE + 6;
  const mIzq = margen || PREV_MARGEN;
  const mDer = margen || PREV_MARGEN;
  const extra = extraSangriaPreview(margen);

  if (forma === 'circulo') {
    const d = altoCaja;
    const x = posicion === 'centro' ? (PREV_W - d) / 2
      : posicion === 'derecha' ? PREV_W - mDer - d
        : mIzq;
    // El logo entra en el CUADRADO INSCRIPTO (lado = d/raiz(2)), no en el
    // bounding box: si no, sus esquinas se salen de la curva.
    const lado = d / Math.SQRT2 - (PREV_BORDE + 4);
    return {
      x, y, w: d, h: d, rx: d / 2,
      logo: { x: x + (d - lado) / 2, y: y + (d - lado) / 2, w: lado, h: lado },
    };
  }

  // La cápsula se achica en proporción cuando la caja es más baja: en el PDF
  // el ancho sale del alto útil por la proporción del logo.
  const h = altoCaja;
  const base = Math.round(PREV_CAPSULA_W * h / PREV_CAJA_H);
  if (posicion === 'centro') {
    const w = base;
    const x = (PREV_W - w) / 2;
    // Cápsula completa: el logo arranca después del casquete redondeado, que
    // mide h/2 de cada lado. Con menos padding sus esquinas se irían al aire.
    return { x, y, w, h, rx: h / 2,
      logo: { x: x + h / 2, y: y + pad, w: w - h, h: h - pad * 2 } };
  }
  const w = base + extra;
  if (posicion === 'derecha') {
    const x = PREV_W + PREV_OFF - w;
    return { x, y, w, h, rx: h / 2,
      logo: { x: x + pad, y: y + pad, w: w - pad - PREV_SANGRIA - extra, h: h - pad * 2 } };
  }
  const x = -PREV_OFF;
  return { x, y, w, h, rx: h / 2,
    logo: { x: x + PREV_SANGRIA + extra, y: y + pad, w: w - PREV_SANGRIA - extra - pad, h: h - pad * 2 } };
};

/** Franja donde entra el título: lo que SOBRA entre la caja y el badge. */
const zonaTituloPreview = (posicion, caja, badgeX, conBadge, margen = null) => {
  const m = margen || PREV_MARGEN;
  if (posicion === 'centro') return [m, caja.x];
  // Sin badge (planilla de tablas) la franja llega hasta el margen, igual que
  // `_zona_titulo` con `corral=None`.
  if (posicion === 'derecha') return [conBadge ? badgeX + PREV_BADGE_W : m, caja.x];
  return [caja.x + caja.w, conBadge ? badgeX : PREV_W - m];
};

const PreviewCabecera = ({
  forma, posicion, vertical = 'colgando', titulo, logoUrl, fondo = '#ffffff', conBadge = true,
  colores = COLORES_EVENTO_DEFAULT, termino = 'Corral',
  bandaH = PREV_BANDA_H, tituloSize = 20, margen = null, despeje = 0,
  // Cabecera externa: la imagen cubre la banda (`object-fit: cover`, como
  // `_draw_image_cover` en el PDF); con `externaTitulo` es una trama y el
  // título va encima, en su franja, y la imagen cubre también el despeje.
  externaUrl = '', externaTitulo = true,
}) => {
  const esExterna = !!externaUrl;
  // Cabecera completa (externa sin título): tampoco lleva el badge.
  const conTitulo = !esExterna || externaTitulo;
  const conBadgeReal = conBadge && conTitulo;
  const cajaSinDespeje = cajaLogoPreview(forma, posicion, vertical, bandaH, margen);
  // Con despeje todo lo que cuelga de la banda baja lo mismo que el título.
  const caja = {
    ...cajaSinDespeje,
    y: cajaSinDespeje.y + despeje,
    logo: { ...cajaSinDespeje.logo, y: cajaSinDespeje.logo.y + despeje },
  };
  // El badge se va SIEMPRE al lado opuesto del logo.
  const m = margen || PREV_MARGEN;
  const badgeX = posicion === 'derecha' ? m : PREV_W - m - PREV_BADGE_W;
  // Sin caja del logo (externa) la franja del título va del margen al badge.
  const [zonaX0, zonaX1] = esExterna
    ? (posicion === 'derecha'
      ? [conBadge ? badgeX + PREV_BADGE_W : m, PREV_W - m]
      : [m, conBadge ? badgeX : PREV_W - m])
    : zonaTituloPreview(posicion, caja, badgeX, conBadge, margen);
  const anchoZona = Math.max(0, zonaX1 - zonaX0 - 20);
  const tituloVisible = textoCortadoPreview(
    titulo || 'NOMBRE DEL REMATE', anchoZona, tituloSize, 1.1,
  );

  return (
    <svg
      viewBox={`0 0 ${PREV_W} ${PREV_H}`}
      width="100%"
      role="img"
      aria-label={`Vista previa de la cabecera: logo en ${FORMA_LABEL[forma]}, ${posicion}, ${VERTICAL_LABEL[vertical]}`}
      style={{ display: 'block', overflow: 'hidden' }}
    >
      {/* El título ya viene recortado con elipsis; el clip es la red por si la
          estimación de ancho se queda corta con una tipografía más ancha. */}
      <clipPath id={PREV_CLIP_ID}>
        <rect x={zonaX0 + 10} y={0} width={anchoZona} height={bandaH + despeje} />
      </clipPath>

      {/* Banda principal + línea secundaria debajo; o el diseño externo
          cubriendo la banda (la trama también el despeje, la completa no:
          ver `_draw_header`). */}
      {esExterna ? (
        <image
          href={externaUrl}
          x={0} y={0} width={PREV_W} height={externaTitulo ? bandaH + despeje : bandaH}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <>
          <rect x={0} y={0} width={PREV_W} height={bandaH + despeje} fill={colores.primario} />
          <rect x={0} y={bandaH + despeje} width={PREV_W} height={PREV_LINEA_H} fill={colores.secundario} />
        </>
      )}

      {conTitulo && (
      <g clipPath={`url(#${PREV_CLIP_ID})`}>
        <text
          x={(zonaX0 + zonaX1) / 2}
          y={despeje + bandaH / 2 + tituloSize * 0.35}
          textAnchor="middle"
          fill="#ffffff"
          fontSize={tituloSize}
          fontWeight={700}
          letterSpacing={1.1}
        >
          {tituloVisible}
        </text>
      </g>
      )}

      {/* Badge del corral: solo en carátulas. La planilla de tablas junta
          varios corrales en una hoja y no lleva badge. */}
      {conBadgeReal && (
        <>
          {/* El badge mide lo que la caja colgando de ESTA banda
              (`_alto_badge_corral(hh)`), y sus textos van a la misma escala. */}
          <rect
            x={badgeX} y={PREV_MARGEN + despeje} width={PREV_BADGE_W} height={cajaColgandoH(bandaH)}
            rx={12} fill="#ffffff" stroke={colores.secundario} strokeWidth={PREV_BORDE}
          />
          <text
            x={badgeX + PREV_BADGE_W / 2} y={despeje + PREV_MARGEN + 22 * (cajaColgandoH(bandaH) / PREV_CAJA_H)} textAnchor="middle"
            fill={PREV_NEGRO} fontSize={11} fontWeight={700} letterSpacing={0.5}
          >
            {termino.toUpperCase()}
          </text>
          <text
            x={badgeX + PREV_BADGE_W / 2} y={despeje + PREV_MARGEN + 56 * (cajaColgandoH(bandaH) / PREV_CAJA_H)} textAnchor="middle"
            fill={PREV_NEGRO} fontSize={29 * (cajaColgandoH(bandaH) / PREV_CAJA_H)} fontWeight={700}
          >
            12
          </text>
        </>
      )}

      {/* Caja del logo. Cuando sangra, el viewBox la recorta igual que el filo
          de la hoja recorta la cápsula impresa.
          El círculo va como <circle> y no como <rect rx>: un rect con el radio
          al máximo depende de que el motor infiera `ry = rx`, y no todos lo
          hacen — ahí sale un cuadrado redondeado en vez de un círculo. */}
      {/* El fondo es el color elegido para la caja (`fondo`); el borde, el
          secundario del micrositio, igual que en el PDF. Con cabecera
          externa no hay caja: el logo lo trae (o no) la imagen. */}
      {esExterna ? null : forma === 'circulo' ? (
        <circle
          cx={caja.x + caja.w / 2} cy={caja.y + caja.h / 2} r={caja.rx}
          fill={fondo} stroke={colores.secundario} strokeWidth={PREV_BORDE}
        />
      ) : (
        <rect
          x={caja.x} y={caja.y} width={caja.w} height={caja.h} rx={caja.rx}
          fill={fondo} stroke={colores.secundario} strokeWidth={PREV_BORDE}
        />
      )}
      {esExterna ? null : logoUrl ? (
        <image
          href={logoUrl}
          x={caja.logo.x} y={caja.logo.y}
          width={caja.logo.w} height={caja.logo.h}
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <text
          x={caja.logo.x + caja.logo.w / 2}
          y={caja.logo.y + caja.logo.h / 2 + 6}
          textAnchor="middle" fill={PREV_GRIS} fontSize={16} fontWeight={700}
          letterSpacing={2}
        >
          LOGO
        </text>
      )}
    </svg>
  );
};


// ─── Componente principal ────────────────────────────────────────────────────
const GenerarPDF = () => {
  const [step, setStep] = useState(0);
  const [generando, setGenerando] = useState(false);
  // Qué salida se está generando (`impresion` | `digital` | el modo), para
  // que el botón que se tocó sea el que gira y el otro quede deshabilitado.
  const [salidaGenerando, setSalidaGenerando] = useState(null);
  // La maquetación guardada de (evento, modo), si hay (2026-10-29): el estado
  // del asistente tal cual se dejó, que vive siete días desde el último
  // guardado. Se ofrece cargarla, no se aplica sola: el productor puede
  // querer arrancar de cero.
  const [maquetacion, setMaquetacion] = useState(null);
  // Si la maquetación guardada se CARGÓ en esta sesión. Generar y «Guardar
  // maquetación» pisan la guardada con lo que hay en pantalla: si hay una
  // guardada y no se cargó, lo que hay en pantalla es la versión vacía y
  // pisarla sin avisar era perder el trabajo (hallazgo #1, 2026-11-02).
  const [maquetacionCargada, setMaquetacionCargada] = useState(false);
  const [guardandoMaq, setGuardandoMaq] = useState(false);

  // Cronometro mientras generando=true (UX modal generacion 2-3min)
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);

  // Paso 1
  const [eventos, setEventos] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [eventoId, setEventoId] = useState(null);
  // Tablas como formato inicial: es el que más se usa el día del remate.
  const [modo, setModo] = useState('tablas');
  // Paso "Estructura". OP1 / OP2 es sólo del catálogo (impresión y digital);
  // los dos radios de abajo también los usa carátulas.
  const [estructura, setEstructura] = useState(ESTRUCTURA_DEFAULT);
  const [depsVista, setDepsVista] = useState(DEPS_VISTA_DEFAULT);
  const [genealogiaVista, setGenealogiaVista] = useState(GENEALOGIA_VISTA_DEFAULT);
  // De qué evento salieron los radios de arriba. El efecto que carga el evento
  // corre también al cambiar de FORMATO (el logo y los colores dependen del
  // modo), y sin esta marca un cambio de impresión a digital pisaba lo que el
  // productor ya había elegido para esta tirada.
  const vistasDelEvento = useRef(null);
  // La genealogía se elige en carátulas (Árbol | Tabla: siempre hay lugar) y
  // en el catálogo (Automático | Tabla): ahí 'arbol' quiere decir "nodos
  // cuando hay lugar", que es sólo la hoja B de OP2 sin familiares con foto;
  // OP1 va siempre con la compacta, su columna de 434pt está diseñada así y
  // el árbol la rompería. "Tabla" fuerza la compacta en todo el catálogo:
  // la misma línea en todas las hojas (usuario, 2026-10-16).
  const eligeGenealogia = modo === 'caratulas' || eligeEstructura(modo);
  const infoGenealogia = modo === 'caratulas' ? GENEALOGIA_VISTA_INFO : GENEALOGIA_CATALOGO_INFO;
  // 'nodos' ("Árbol" del catálogo) no existe en carátulas, donde 'arbol' YA
  // es el árbol literal: al pasar del catálogo a carátulas se lee como
  // 'arbol' (y es lo que viaja), en vez de romper el radio con una clave
  // que su tabla no tiene.
  const genealogiaElegida = infoGenealogia[genealogiaVista] ? genealogiaVista : 'arbol';
  const [granularidad, setGranularidad] = useState('evento');
  // 'corral' por defecto (pedido del usuario, 2026-10-08): las tablas se usan
  // sobre todo el dia del remate, recorriendo los corrales en orden.
  const [ordenTablas, setOrdenTablas] = useState('corral');

  // Paso "Cabecera" — todos los formatos. Forma, posición, imagen y fondo de
  // la caja del logo.
  const [cabeceraLogo, setCabeceraLogo] = useState(CABECERA_DEFAULT);
  // Generada (la de siempre) o externa (el productor sube el diseño). Es un
  // estado aparte de `cabeceraLogo.externa` para poder elegir «Externa» antes
  // de haber subido nada; al generar lo único que viaja es la URL.
  const [tipoCabecera, setTipoCabecera] = useState('generada');
  // Tamaño en píxeles de la externa subida, para avisar si no viene en la
  // proporción de la banda (se recorta COVER, como en el PDF).
  const [externaDims, setExternaDims] = useState(null);
  useEffect(() => {
    setExternaDims(null);
    if (!cabeceraLogo.externa) return undefined;
    let vigente = true;
    const img = new Image();
    img.onload = () => { if (vigente) setExternaDims({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.src = fixUrl(cabeceraLogo.externa);
    return () => { vigente = false; };
  }, [cabeceraLogo.externa]);
  // Los tres logos del evento para la vista previa, y sus colores de
  // micrositio para pintar la caja (ver el efecto más abajo). Mobile e iso
  // además deciden si su opción se puede elegir.
  const [imagenesEvento, setImagenesEvento] = useState(SIN_IMAGENES);
  const [cabeceraEvento, setCabeceraEvento] = useState(COLORES_PUJA);
  const [logoCargando, setLogoCargando] = useState(false);

  // Paso 2 — presentación
  const [presentacion, setPresentacion] = useState({
    activa: false,
    texto: '',
    imagen_url: '',
  });

  // Paso 2 — secciones ordenables, en una de las dos vistas (ver
  // `VISTA_CONTENIDO_INFO`). Cambiar de vista recarga la lista y descarta los
  // grupos de a dos: están armados sobre otras secciones.
  const [vistaContenido, setVistaContenido] = useState('categoria');
  const [secciones, setSecciones] = useState([]);
  const [loadingSecciones, setLoadingSecciones] = useState(false);

  // Paso 2 — grupos de animales (session-only, no persiste en BD)
  // Shape: { "idCategoria_idRaza": [{ id, label, animalIds: [] }] }
  const [grupos, setGrupos] = useState({});

  const crearGrupo = useCallback((key, animalIds) => {
    setGrupos(prev => {
      const existentes = prev[key] || [];
      return {
        ...prev,
        [key]: [...existentes, { id: uid(), label: `Grupo ${existentes.length + 1}`, animalIds }],
      };
    });
  }, []);

  const editarGrupo = useCallback((key, grupoId, animalIds) => {
    setGrupos(prev => ({
      ...prev,
      [key]: (prev[key] || []).map(g => g.id === grupoId ? { ...g, animalIds } : g),
    }));
  }, []);

  const eliminarGrupo = useCallback((key, grupoId) => {
    setGrupos(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(g => g.id !== grupoId),
    }));
  }, []);

  // ── Cargar secciones de un evento ────────────────────────────────────────
  const cargarSecciones = useCallback(async (id, vista = 'categoria', guardadas = null) => {
    if (!id) return;
    setSecciones([]);
    setLoadingSecciones(true);
    try {
      const r = await pdfService.getSecciones(id, vista);
      // Todas las secciones arrancan activas salvo los terminos y condiciones:
      // el usuario los apaga casi siempre (2026-10-08), asi que arrancan en
      // off y el que los quiere los prende.
      const frescas = r.data.secciones.map(s => ({ ...s, _uid: uid(), activa: s.tipo !== 'condiciones' }));
      setSecciones(guardadas ? fusionarSecciones(frescas, guardadas) : frescas);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al cargar secciones del evento');
    } finally {
      setLoadingSecciones(false);
    }
  }, []);

  // ── Cargar eventos al montar y auto-seleccionar el último activo ──────────
  useEffect(() => {
    setLoadingEventos(true);
    // lista-corta: solo 6 campos por evento (antes getAll cargaba ~30).
    eventoService.getListaCorta({ estado: 1 })
      .then(r => {
        const lista = eventosDeListaCorta(r);
        setEventos(lista);
        // lista-corta usa estado_evento (no estado_activo).
        const activo = lista.find(e => e.estado_evento === 1);
        const inicial = activo || lista[0];
        if (inicial) {
          setEventoId(inicial.id_evento);
          cargarSecciones(inicial.id_evento);
        }
      })
      .catch(err => message.error(err.response?.data?.error || 'Error al cargar eventos'))
      .finally(() => setLoadingEventos(false));
  }, [cargarSecciones]);

  // Cronometro del modal: arranca cuando generando=true, se resetea al
  // terminar. Solo informativo — la request sigue siendo sincrona.
  useEffect(() => {
    if (!generando) {
      setTiempoTranscurrido(0);
      return;
    }
    const interval = setInterval(() => {
      setTiempoTranscurrido(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [generando]);

  // ── Logo del evento para la vista previa de la cabecera ───────────────────
  // `GET /eventos/lista-corta` NO trae `img_evento`: devuelve 6 campos a
  // propósito y sumarle uno le engorda el payload a los once selectores que la
  // usan. Así que el preview pide el evento completo. Hoy todos los formatos
  // tienen paso de cabecera, y `tieneCabecera` queda como la única puerta por
  // si alguno vuelve a no tenerlo.
  useEffect(() => {
    if (!tieneCabecera(modo) || !eventoId) {
      setImagenesEvento(SIN_IMAGENES);
      setCabeceraEvento(COLORES_PUJA);
      return undefined;
    }
    // Cambiar de evento rápido deja dos requests en vuelo: sin este flag, la
    // más lenta pisa el logo de la que el usuario está mirando.
    let vigente = true;
    setLogoCargando(true);
    eventoService.getById(eventoId)
      .then(({ data }) => {
        if (!vigente) return;
        const cfg = data?.configuracion_micrositio || {};
        const imagenes = {
          logo: fixUrl(data?.img_evento || ''),
          mobile: fixUrl(cfg.logo_mobile_url || ''),
          iso: fixUrl(cfg.logo_iso_url || ''),
        };
        setImagenesEvento(imagenes);
        setCabeceraEvento({
          primario: cfg.color_primary || COLORES_EVENTO_DEFAULT.primario,
          secundario: cfg.color_secondary || COLORES_EVENTO_DEFAULT.secundario,
          termino: cfg.termino_lote === 'lote' ? 'Lote' : 'Corral',
        });
        // Los radios de genealogía y DEP's arrancan con lo elegido para el
        // micrositio de ESTE evento; el productor los cambia acá si quiere.
        // Sólo al cambiar de evento: un cambio de formato no los pisa.
        if (vistasDelEvento.current !== eventoId) {
          vistasDelEvento.current = eventoId;
          const vistas = vistasDeConfig(cfg);
          setGenealogiaVista(vistas.genealogia);
          setDepsVista(vistas.deps);
        }
        // El evento nuevo no tiene la imagen elegida: la elección anterior ya
        // no vale y el backend igual caería al principal, así que la pantalla
        // no miente.
        setCabeceraLogo(c => (imagenes[c.imagen] || c.imagen === 'logo' ? c : { ...c, imagen: 'logo' }));
      })
      // Sin logo el preview dibuja la forma vacía, que es exactamente lo que
      // hace el PDF: un error acá no justifica un cartel rojo en pantalla.
      .catch(() => {
        if (!vigente) return;
        setImagenesEvento(SIN_IMAGENES);
        setCabeceraEvento(COLORES_EVENTO_DEFAULT);
      })
      .finally(() => { if (vigente) setLogoCargando(false); });
    return () => { vigente = false; };
  }, [modo, eventoId]);

  // ── El índice del paso, cuando los pasos cambian ──────────────────────────
  // Los pasos son dinámicos: al salir del catálogo (impresión o digital)
  // desaparece "Estructura" y el índice guardado puede apuntar a un paso que
  // ya no existe. En la práctica el formato se elige en el paso 0, así que
  // esto casi nunca dispara — pero sin el clamp `pasos[step]` queda undefined
  // y el render explota.
  useEffect(() => {
    setStep(s => (s < pasosDe(modo).length ? s : 0));
  }, [modo]);

  // La vista por corral es sólo del catálogo: si el productor la eligió y
  // después pasa a tablas o carátulas, el paso vuelve a la vista de siempre
  // (esos formatos no leen las secciones, pero mostrarles corrales sueltos
  // sería prometer un orden que no van a respetar).
  useEffect(() => {
    if (!eligeEstructura(modo) && vistaContenido === 'corral') {
      setVistaContenido('categoria');
      setGrupos({});
      cargarSecciones(eventoId, 'categoria');
    }
  }, [modo, vistaContenido, eventoId, cargarSecciones]);

  const formatTiempo = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}m ${ss.toString().padStart(2, '0')}s`;
  };

  // ── La maquetación guardada de (evento, modo) ─────────────────────────────
  // Se consulta al cambiar cualquiera de los dos; un 404 es "no hay", no un
  // error que mostrar.
  useEffect(() => {
    if (!eventoId) {
      setMaquetacion(null);
      return undefined;
    }
    let vigente = true;
    setMaquetacionCargada(false);
    pdfService.getMaquetacion(eventoId, modo)
      .then(({ data }) => { if (vigente) setMaquetacion(data.maquetacion); })
      .catch(() => { if (vigente) setMaquetacion(null); });
    return () => { vigente = false; };
  }, [eventoId, modo]);

  // Hay una guardada que NO se cargó: lo que se guarde o genere la va a pisar.
  const pisariaLaGuardada = Boolean(maquetacion) && !maquetacionCargada;

  // Pide confirmación antes de pisar la maquetación guardada. Resuelve `true`
  // si se puede seguir (no hay riesgo, o el usuario eligió pisarla).
  const confirmarPisarGuardada = (accion) => new Promise((resolve) => {
    if (!pisariaLaGuardada) {
      resolve(true);
      return;
    }
    Modal.confirm({
      title: 'Vas a pisar la maquetación guardada',
      width: 520,
      content: (
        <div>
          <p>
            Para este evento hay una maquetación guardada el {fechaCorta(maquetacion.actualizado_en)}{' '}
            (orden, portadas, publicidades, cabecera) que <strong>no cargaste</strong>.
            Si {accion} ahora, se reemplaza por lo que tenés en pantalla y lo guardado se pierde.
          </p>
          <p style={{ marginBottom: 0 }}>
            Si querías seguir sobre lo guardado, cancelá y apretá «Cargar maquetación» en el paso Evento.
          </p>
        </div>
      ),
      okText: `Pisar la guardada y ${accion}`,
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  // El estado del asistente que se guarda: todo lo que el productor eligió,
  // sin los `_uid` (son de la sesión). Las secciones viajan ENTERAS, con sus
  // portadas, publicidades y textos; al cargar se fusionan con las frescas
  // del evento (`fusionarSecciones`).
  const estadoActual = () => ({
    modo, estructura, depsVista, genealogiaVista: genealogiaElegida, granularidad, ordenTablas,
    cabeceraLogo, presentacion, vistaContenido,
    secciones: secciones.map(({ _uid, ...s }) => s),
    grupos,
  });

  const guardarMaquetacion = async (silencioso = false) => {
    if (!eventoId) return;
    // El botón «Guardar maquetación» pregunta; al generar ya se preguntó.
    if (!silencioso && !(await confirmarPisarGuardada('guardar'))) return;
    setGuardandoMaq(true);
    try {
      const { data } = await pdfService.guardarMaquetacion(eventoId, { modo, estado: estadoActual() });
      setMaquetacion(data.maquetacion);
      setMaquetacionCargada(true);
      if (!silencioso) message.success(`Maquetación guardada. Se conserva hasta el ${fechaCorta(data.maquetacion.vence_en)}.`);
    } catch (err) {
      if (!silencioso) message.error(err.response?.data?.error || 'No se pudo guardar la maquetación');
    } finally {
      setGuardandoMaq(false);
    }
  };

  // Vuelve a dejar el asistente como estaba al guardar. Las secciones se
  // piden de nuevo al backend (pueden haber entrado o salido animales) y se
  // fusionan con las guardadas; los grupos de a dos se restauran tal cual y
  // el backend ignora los animales que ya no estén.
  const aplicarMaquetacion = (m) => {
    const e = m?.estado || {};
    if (e.estructura) setEstructura(e.estructura);
    if (e.depsVista) setDepsVista(e.depsVista);
    if (e.genealogiaVista) setGenealogiaVista(e.genealogiaVista);
    if (e.granularidad) setGranularidad(e.granularidad);
    if (e.ordenTablas) setOrdenTablas(e.ordenTablas);
    if (e.cabeceraLogo) {
      setCabeceraLogo(c => ({ ...c, ...e.cabeceraLogo }));
      setTipoCabecera(e.cabeceraLogo.externa ? 'externa' : 'generada');
    }
    if (e.presentacion) setPresentacion(e.presentacion);
    const vista = e.vistaContenido || 'categoria';
    setVistaContenido(vista);
    setGrupos(e.grupos || {});
    cargarSecciones(eventoId, vista, e.secciones || []);
    setMaquetacionCargada(true);
    message.success('Maquetación cargada');
  };

  const descartarMaquetacion = async () => {
    try {
      await pdfService.borrarMaquetacion(eventoId, modo);
      setMaquetacion(null);
      setMaquetacionCargada(false);
      message.success('Maquetación descartada');
    } catch (err) {
      message.error(err.response?.data?.error || 'No se pudo descartar la maquetación');
    }
  };

  // ── Cuando el usuario cambia el evento manualmente ────────────────────────
  const handleSelectEvento = (id) => {
    setEventoId(id);
    setGrupos({});
    cargarSecciones(id, vistaContenido);
  };

  const handleSelectVista = (vista) => {
    setVistaContenido(vista);
    setGrupos({});
    cargarSecciones(eventoId, vista);
  };

  // ── Mover sección ─────────────────────────────────────────────────────────
  const mover = (idx, dir) => {
    setSecciones(prev => {
      const arr = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return arr;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return arr;
    });
  };

  // ── Mover a una posición (1-based en pantalla) ──────────────────────────
  // Con sesenta corrales, subir uno de a una flecha es un castigo: el ítem
  // dice en qué posición está y deja escribir a cuál va.
  const moverA = (idx, destino) => {
    setSecciones(prev => {
      const to = Math.max(0, Math.min(prev.length - 1, destino));
      if (to === idx) return prev;
      const arr = [...prev];
      const [item] = arr.splice(idx, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };

  const eliminar = (idx) => setSecciones(prev => prev.filter((_, i) => i !== idx));

  const actualizarSeccion = (idx, changes) => {
    setSecciones(prev => prev.map((s, i) => i === idx ? { ...s, ...changes } : s));
  };

  // ── Agregar publicidad ────────────────────────────────────────────────────
  const agregarPublicidad = () => {
    setSecciones(prev => [...prev, {
      _uid: uid(),
      tipo: 'publicidad',
      subtipo: 'texto',
      titulo: '',
      subtitulo: '',
      imagen_url: '',
      activa: true,
    }]);
  };

  // ── Agregar portada suelta (vista por corral) ─────────────────────────────
  // Lo mismo que lleva la portada de un grupo: título e imagen de fondo.
  const agregarPortada = () => {
    setSecciones(prev => [...prev, {
      _uid: uid(),
      tipo: 'portada',
      titulo: '',
      imagen_url: '',
      activa: true,
    }]);
  };

  // ── Subir imagen o PDF para una portada o publicidad ──────────────────────
  // Hasta 60 MB. Un PDF vuelve como la imagen de su primera página: el
  // catálogo sólo dibuja imágenes. Queda en el servidor hasta siete días
  // después del último guardado de la maquetación que lo use.
  const subirArchivo = async (file, aplicar) => {
    try {
      const { data } = await pdfService.subirArchivo(file);
      aplicar(data.url);
      message.success(data.tipo === 'pdf' ? 'PDF subido: se usa su primera página como imagen' : 'Imagen subida');
    } catch (err) {
      message.error(err.response?.data?.error || 'No se pudo subir el archivo');
    }
    return false; // evitar upload automático de antd
  };
  const handleUploadImagen = (file, idx) => subirArchivo(file, (url) => actualizarSeccion(idx, { imagen_url: url }));
  const handleUploadPresentacion = (file) => subirArchivo(file, (url) => setPresentacion(p => ({ ...p, imagen_url: url })));
  const handleUploadCabecera = (file) => subirArchivo(file, (url) => setCabeceraLogo(c => ({ ...c, externa: url })));
  const elegirTipoCabecera = (tipo) => {
    setTipoCabecera(tipo);
    // Volver a la generada suelta la URL: lo que viaja al backend es lo que
    // se ve en el preview, sin estados a medias.
    if (tipo === 'generada') setCabeceraLogo(c => ({ ...c, externa: '' }));
  };

  // ── Generar PDF ───────────────────────────────────────────────────────────
  // `salida` viene de los dos botones del catálogo (`impresion` | `digital`);
  // tablas y carátulas generan con su propio modo.
  const handleGenerar = async (salida) => {
    if (!eventoId) {
      message.warning('Seleccioná un evento primero');
      return;
    }
    // Generar guarda sola la maquetación: si hay una guardada sin cargar, se
    // avisa ANTES de los dos minutos de generación, no después.
    if (!(await confirmarPisarGuardada('generar'))) return;
    const modoGeneracion = SALIDA_INFO[salida] ? salida : modo;
    setGenerando(true);
    setSalidaGenerando(modoGeneracion);
    try {
      const seccionesActivas = secciones
        .filter(s => s.activa)
        .map(({ _uid, activa, disponible, total_animales, ...rest }) => {
          if (tieneAnimales(rest)) {
            return { ...rest, grupos: grupos[claveGrupos(rest)] || [] };
          }
          return rest;
        });

      const resp = await pdfService.generar({
        id_evento: eventoId,
        modo: modoGeneracion,
        presentacion,
        secciones: seccionesActivas,
        // Granularidad es solo de carátulas; la cabecera la llevan los cuatro
        // formatos; el orden es solo de tablas; OP1 / OP2 solo el catálogo;
        // genealogía y DEP's, el catálogo y carátulas.
        ...(modo === 'caratulas' && { granularidad }),
        ...(tieneCabecera(modo) && { cabecera_logo: cabeceraLogo }),
        ...(modo === 'tablas' && { orden: ordenTablas }),
        ...(eligeEstructura(modo) && { estructura }),
        ...(tieneEstructura(modo) && { deps_vista: depsVista, genealogia_vista: genealogiaElegida }),
      });

      // Trigger descarga. El anchor va al DOM y la URL se revoca diferida: si
      // se revoca en el mismo tick, la descarga puede morir a mitad de camino
      // y perderíamos los 2-3 minutos de generación que el usuario esperó.
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const evento = eventos.find(e => e.id_evento === eventoId);
      a.href = url;
      a.download = `catalogo_${evento?.nombre_evento?.replace(/\s+/g, '_') || eventoId}_${modoGeneracion}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success('PDF generado correctamente');
      // Generar es la señal de que la maquetación vale: se guarda sola, sin
      // cartel, para que la próxima vuelta arranque de acá.
      guardarMaquetacion(true);
    } catch (err) {
      // En un timeout no hay err.response: sin este caso el usuario espera
      // varios minutos y recibe un error genérico que no explica nada.
      if (err.code === 'ECONNABORTED') {
        message.error('La generación superó el tiempo de espera. Probá con menos secciones o volvé a intentar.');
        return;
      }
      // El interceptor de api.js ya desempaqueta el Blob de error y reescribe
      // err.response.data con el JSON parseado — acá no hay que leerlo de nuevo.
      message.error(err.response?.data?.error || 'Error al generar el PDF');
    } finally {
      setGenerando(false);
      setSalidaGenerando(null);
    }
  };

  // ─── Steps content ──────────────────────────────────────────────────────

  const step0 = (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>Evento y formato</Title>

      <div style={{ marginBottom: 24, maxWidth: 600 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Evento</Text>
        <Select
          style={{ width: '100%' }}
          placeholder={loadingEventos ? 'Cargando eventos...' : 'Seleccioná un evento'}
          value={eventoId}
          onChange={handleSelectEvento}
          showSearch
          optionFilterProp="children"
          loading={loadingEventos}
          disabled={loadingEventos}
        >
          {eventos.map(e => (
            <Option key={e.id_evento} value={e.id_evento}>{e.nombre_evento}</Option>
          ))}
        </Select>
      </div>

      {maquetacion && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24, maxWidth: 600 }}
          message={`Hay una maquetación guardada de ${MODO_INFO[modo]?.titulo || modo} para este evento`}
          description={(
            <div>
              Guardada el {fechaCorta(maquetacion.actualizado_en)}. Se conserva hasta el {fechaCorta(maquetacion.vence_en)};
              cada guardado renueva los {maquetacion.dias_vigencia} días, y al vencer se borra con sus portadas y publicidades.
              <Space style={{ marginTop: 10 }}>
                <Button size="small" type="primary" onClick={() => aplicarMaquetacion(maquetacion)}>
                  Cargar maquetación
                </Button>
                <Popconfirm
                  title="Descartar la maquetación guardada"
                  description="Se borra junto con las portadas y publicidades que subiste para ella."
                  okText="Descartar"
                  cancelText="Cancelar"
                  onConfirm={descartarMaquetacion}
                >
                  <Button size="small" danger>Descartar</Button>
                </Popconfirm>
              </Space>
            </div>
          )}
        />
      )}

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>Formato de salida</Text>
        <Row gutter={[16, 16]}>
          {Object.entries(MODO_INFO).map(([clave, info]) => {
            const Icono = info.icono;
            const activo = modo === clave;
            const bloqueado = !!info.proximamente;
            return (
              <Col xs={12} md={8} key={clave}>
                <Tooltip title={bloqueado ? 'Próximamente disponible' : ''}>
                  <Card
                    hoverable={!bloqueado}
                    onClick={() => !bloqueado && setModo(clave)}
                    style={{
                      position: 'relative',
                      border: activo ? '2px solid #1890ff' : '1px solid #e8e8e8',
                      cursor: bloqueado ? 'not-allowed' : 'pointer',
                      textAlign: 'center', height: '100%',
                      opacity: bloqueado ? 0.55 : 1,
                    }}
                    styles={{ body: { padding: 16 } }}
                  >
                    {bloqueado && (
                      <Tag
                        color="default"
                        style={{ position: 'absolute', top: 8, right: 8, fontSize: 10 }}
                      >
                        Próximamente
                      </Tag>
                    )}
                    <Icono style={{ fontSize: 28, color: activo ? '#1890ff' : '#bbb', marginBottom: 8, display: 'block' }} />
                    <Text strong>{info.titulo}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>{info.desc}</Text>
                  </Card>
                </Tooltip>
              </Col>
            );
          })}
        </Row>

        {/* Orden — solo aplica a tablas */}
        {modo === 'tablas' && (
          <div style={{ marginTop: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Cómo ordenamos las tablas?</Text>
            <Radio.Group
              value={ordenTablas}
              onChange={e => setOrdenTablas(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="corral">Por corral</Radio.Button>
              <Radio.Button value="categoria">Por categoría</Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              {ORDEN_TABLAS_INFO[ordenTablas]}
            </Text>
          </div>
        )}

        {/* Granularidad — solo aplica a carátulas */}
        {modo === 'caratulas' && (
          <div style={{ marginTop: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Cómo agrupamos las carátulas?</Text>
            <Radio.Group
              value={granularidad}
              onChange={e => setGranularidad(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="evento">Como se muestra en el evento</Radio.Button>
              <Radio.Button value="corral">Una por corral</Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              {GRANULARIDAD_INFO[granularidad]}
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  // Paso "Estructura" — catálogo (impresión y digital) y carátulas, donde se
  // titula "Ficha del cartel" y no ofrece OP1 / OP2 (ver `pasosDe`).
  const stepEstructura = (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {modo === 'caratulas' ? 'Ficha del cartel' : 'Estructura del catálogo'}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        {modo === 'caratulas'
          ? 'Elegí cómo se leen la genealogía y los DEP\'s en el cartel de cada animal. Arranca con lo que elegiste para el micrositio.'
          : 'Elegí cómo se arma cada animal en el catálogo y cómo se leen sus DEP\'s.'}
      </Text>

      {eligeEstructura(modo) && (
        <>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Estructura</Text>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {Object.entries(ESTRUCTURA_INFO).map(([clave, info]) => {
              const activo = estructura === clave;
              return (
                <Col xs={24} md={12} key={clave}>
                  <Card
                    hoverable
                    onClick={() => setEstructura(clave)}
                    style={{
                      cursor: 'pointer',
                      border: activo ? '2px solid #1890ff' : '1px solid #e8e8e8',
                      background: activo ? '#f0f7ff' : '#fff',
                    }}
                    styles={{ body: { padding: 14 } }}
                  >
                    <Text strong style={{ display: 'block' }}>{info.label}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{info.desc}</Text>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      {eligeGenealogia && (
        <>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Cómo querés ver la genealogía?</Text>
          <Radio.Group
            value={genealogiaElegida}
            onChange={e => setGenealogiaVista(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            {Object.entries(infoGenealogia).map(([clave, info]) => (
              <Radio.Button value={clave} key={clave}>{info.label}</Radio.Button>
            ))}
          </Radio.Group>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, marginBottom: 24, fontSize: 12 }}>
            {infoGenealogia[genealogiaElegida].desc}
          </Text>
        </>
      )}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Cómo querés ver los DEP's?</Text>
      <Radio.Group
        value={depsVista}
        onChange={e => setDepsVista(e.target.value)}
        optionType="button"
        buttonStyle="solid"
      >
        {Object.entries(DEPS_VISTA_INFO).map(([clave, info]) => (
          <Radio.Button value={clave} key={clave}>{info.label}</Radio.Button>
        ))}
      </Radio.Group>
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        {DEPS_VISTA_INFO[depsVista].desc}
      </Text>
    </div>
  );

  // Paso "Cabecera" — todos los formatos (ver `pasosDe`). El radio de fondo
  // tiene cuatro posiciones pero el estado guarda tres nombres o un hex:
  // "Otro" es el hex, y el picker edita ese hex.
  const fondoEsOtro = !FONDOS_CON_NOMBRE.includes(cabeceraLogo.fondo);
  const imagenCabeceraUrl = imagenesEvento[cabeceraLogo.imagen] || '';
  // Tablas no lleva badge de corral (junta varios corrales) y OP1 tampoco: su
  // corral va en un bloque en el cuerpo. El cartel y la hoja B de OP2 sí.
  const esCartel = modo === 'caratulas';
  const conBadge = esCartel || (eligeEstructura(modo) && estructura === 'op2');
  const tituloCabecera = esCartel ? 'Cabecera del cartel'
    : modo === 'tablas' ? 'Cabecera de la planilla' : 'Cabecera del catálogo';
  const bandaHCabecera = eligeEstructura(modo) ? PREV_BANDA_H_CATALOGO : PREV_BANDA_H;
  const esExterna = tipoCabecera === 'externa';
  const externaUrl = esExterna ? fixUrl(cabeceraLogo.externa || '') : '';
  const medidasExterna = medidasCabeceraExterna(bandaHCabecera);
  // Proporción distinta a la de la banda → se recorta (COVER) por el lado
  // que sobra; se avisa, no se frena: una trama se recorta sin perder nada.
  const externaRecorta = externaDims
    && Math.abs(externaDims.w / externaDims.h - medidasExterna.proporcion) / medidasExterna.proporcion > 0.03;
  const resumenCabecera = cabeceraLogo.externa
    ? `diseño propio · ${cabeceraLogo.externa_titulo !== false ? 'con el nombre del remate encima' : 'cabecera completa'}`
    : `${FORMA_LABEL[cabeceraLogo.forma]} · ${cabeceraLogo.posicion} · ${VERTICAL_LABEL[cabeceraLogo.vertical]} · ${IMAGEN_LABEL[cabeceraLogo.imagen]} · fondo ${cabeceraLogo.fondo}`;
  const stepCabecera = (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>{tituloCabecera}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {conBadge
          ? 'Elegí cómo va la caja con el logo en cada hoja, o subí tu propio diseño. El badge del corral se acomoda del otro lado.'
          : 'Elegí cómo va la caja con el logo en cada hoja, o subí tu propio diseño. El nombre del remate ocupa el resto de la banda.'}
      </Text>

      <Radio.Group
        value={tipoCabecera}
        onChange={e => elegirTipoCabecera(e.target.value)}
        optionType="button"
        buttonStyle="solid"
        style={{ marginBottom: 20 }}
      >
        <Radio.Button value="generada">Generada por Puja</Radio.Button>
        <Radio.Button value="externa">Diseño propio</Radio.Button>
      </Radio.Group>

      {esExterna && (
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Archivo de la cabecera</Text>
              <Space wrap>
                <Upload accept="image/*,.pdf" showUploadList={false} beforeUpload={handleUploadCabecera}>
                  <Button icon={<UploadOutlined />}>
                    {cabeceraLogo.externa ? 'Reemplazar' : 'Subir imagen o PDF'}
                  </Button>
                </Upload>
                {cabeceraLogo.externa && (
                  <Button onClick={() => setCabeceraLogo(c => ({ ...c, externa: '' }))}>Quitar</Button>
                )}
              </Space>
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                Medida: <Text code>{medidasExterna.px}</Text> a 300 DPI ({medidasExterna.cm}, el ancho de la hoja por el alto de la banda).
                JPG, PNG o PDF de hasta 60 MB; de un PDF se usa la primera página.
                {eligeEstructura(modo) && ' Para imprenta dejá 1 cm libre arriba y 1,5 cm a los costados: es el margen del anillado. Si va en CMYK, se respeta.'}
                {' '}El pie de página sigue siendo el de Puja.
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Nombre del remate</Text>
              <Space align="center">
                <Switch
                  checked={cabeceraLogo.externa_titulo !== false}
                  onChange={v => setCabeceraLogo(c => ({ ...c, externa_titulo: v }))}
                />
                <Text>{cabeceraLogo.externa_titulo !== false ? 'Encima de la imagen' : `No va: lo trae la imagen${conBadge ? ' (ni el badge del corral)' : ''}`}</Text>
              </Space>
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                Prendido, la imagen es una trama de fondo y el nombre del remate se escribe encima, en blanco{conBadge ? ', con el badge del corral' : ''}.
                Apagado, la imagen es la cabecera completa, con el nombre y el logo ya puestos por el diseñador{conBadge ? ': sin nombre y sin badge' : ''}.
              </Text>
            </Col>
          </Row>
          {externaRecorta && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={`La imagen mide ${externaDims.w} × ${externaDims.h} px y la banda es ${medidasExterna.px}: se va a recortar por el lado que sobra, centrada. Mirá la vista previa.`}
            />
          )}
        </div>
      )}

      <Row gutter={[24, 16]} style={{ marginBottom: 24, display: esExterna ? 'none' : undefined }}>
        <Col xs={24} md={10}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Forma</Text>
          <Radio.Group
            value={cabeceraLogo.forma}
            onChange={e => setCabeceraLogo(c => ({ ...c, forma: e.target.value }))}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="capsula">Cápsula</Radio.Button>
            <Radio.Button value="circulo">Círculo</Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} md={14}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Posición</Text>
          <Radio.Group
            value={cabeceraLogo.posicion}
            onChange={e => setCabeceraLogo(c => ({ ...c, posicion: e.target.value }))}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="izquierda">Izquierda</Radio.Button>
            <Radio.Button value="centro">Centro</Radio.Button>
            <Radio.Button value="derecha">Derecha</Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} md={24}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Altura de la caja</Text>
          <Radio.Group
            value={cabeceraLogo.vertical}
            onChange={e => setCabeceraLogo(c => ({ ...c, vertical: e.target.value }))}
            optionType="button"
            buttonStyle="solid"
          >
            {VERTICALES_CABECERA.map(({ valor, label, tip }) => (
              <Tooltip key={valor} title={tip}>
                <Radio.Button value={valor}>{label}</Radio.Button>
              </Tooltip>
            ))}
          </Radio.Group>
        </Col>
        <Col xs={24} md={10}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Imagen</Text>
          <Radio.Group
            value={cabeceraLogo.imagen}
            onChange={e => setCabeceraLogo(c => ({ ...c, imagen: e.target.value }))}
            optionType="button"
            buttonStyle="solid"
          >
            {/* Sin la imagen cargada el botón se ve pero no se elige: así el
                productor sabe que la opción existe y dónde cargarla. El
                principal siempre se puede elegir. */}
            {IMAGENES_CABECERA.map(({ valor, label }) => {
              const falta = valor !== 'logo' && !imagenesEvento[valor];
              return (
                <Tooltip key={valor} title={falta ? `Cargá el ${IMAGEN_LABEL[valor]} en Herramientas → Micrositio → Estructura` : ''}>
                  <Radio.Button value={valor} disabled={falta}>{label}</Radio.Button>
                </Tooltip>
              );
            })}
          </Radio.Group>
        </Col>
        <Col xs={24} md={14}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Fondo de la caja</Text>
          <Space wrap>
            <Radio.Group
              value={fondoEsOtro ? 'otro' : cabeceraLogo.fondo}
              onChange={e => setCabeceraLogo(c => ({
                ...c,
                fondo: e.target.value === 'otro' ? FONDO_OTRO_INICIAL : e.target.value,
              }))}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="blanco">Blanco</Radio.Button>
              <Radio.Button value="primario">Primario</Radio.Button>
              <Radio.Button value="secundario">Secundario</Radio.Button>
              <Radio.Button value="otro">Otro</Radio.Button>
            </Radio.Group>
            {/* Sin alfa: el PDF no tiene transparencia y el backend solo
                acepta #rrggbb; un hex de 8 dígitos caería a blanco sin aviso. */}
            {fondoEsOtro && (
              <ColorPicker
                value={cabeceraLogo.fondo}
                onChange={color => setCabeceraLogo(c => ({ ...c, fondo: color.toHexString() }))}
                disabledAlpha
                showText
                size="small"
              />
            )}
          </Space>
        </Col>
      </Row>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>Vista previa</Text>
      <div style={{
        border: '1px solid #e8e8e8', borderRadius: 8,
        padding: 12, background: '#fafafa',
      }}>
        <div style={{ background: '#fff', borderRadius: 4, overflow: 'hidden' }}>
          <PreviewCabecera
            forma={cabeceraLogo.forma}
            posicion={cabeceraLogo.posicion}
            vertical={cabeceraLogo.vertical}
            externaUrl={externaUrl}
            externaTitulo={cabeceraLogo.externa_titulo !== false}
            bandaH={bandaHCabecera}
            margen={eligeEstructura(modo) ? PREV_MARGEN_ANILLADO : null}
            despeje={eligeEstructura(modo) ? PREV_DESPEJE_ANILLADO : 0}
            tituloSize={eligeEstructura(modo) ? PREV_TITULO_CATALOGO : 20}
            titulo={eventos.find(e => e.id_evento === eventoId)?.nombre_evento}
            logoUrl={imagenCabeceraUrl}
            fondo={colorFondoPreview(cabeceraLogo.fondo, cabeceraEvento)}
            conBadge={conBadge}
            colores={cabeceraEvento}
            termino={cabeceraEvento.termino}
          />
        </div>
      </div>
      {!esExterna && !logoCargando && !imagenCabeceraUrl && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Este evento no tiene {IMAGEN_LABEL[cabeceraLogo.imagen]} cargado: va a salir la forma vacía.
        </Text>
      )}
      {esExterna && !cabeceraLogo.externa && (
        <Text type="warning" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Todavía no subiste el diseño: hasta entonces sale la cabecera generada por Puja.
        </Text>
      )}
    </div>
  );

  const step1 = (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>Portada y contenido</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Configurá la portada y el orden de las secciones. Usá las flechas para reordenar.
      </Text>

      {/* Las carátulas son carteles sueltos por corral: el servicio ignora
          portada, secciones y grupos. Avisamos antes de que los configure. */}
      {modo === 'caratulas' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message="Las carátulas no usan portada ni secciones"
          description="Este formato genera un cartel por corral con todos los animales del evento. Lo que configures acá no se aplica; pasá directamente al resumen."
        />
      )}

      {/* ── Presentación ── */}
      <Card
        size="small"
        style={{ marginBottom: 16, border: presentacion.activa ? '1px solid #1890ff' : '1px solid #e8e8e8' }}
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <Text strong>Portada / Presentación</Text>
            <Tag color="blue">Siempre primera</Tag>
          </Space>
        }
        extra={
          <Switch
            size="small"
            checked={presentacion.activa}
            onChange={v => setPresentacion(p => ({ ...p, activa: v }))}
          />
        }
      >
        {presentacion.activa && (
          <div>
            <Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Texto de presentación</Text>
            <div style={{ marginBottom: 10 }}>
              <RichTextEditor
                value={presentacion.texto || ''}
                onChange={v => setPresentacion(p => ({ ...p, texto: v }))}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
              La portada muestra hasta 8 líneas centradas. Los títulos y las negritas se respetan.
            </Text>
            <Text style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Imagen de fondo (URL, o subí una imagen o un PDF)</Text>
            <Row gutter={8} align="middle">
              <Col flex="auto">
                <Input
                  placeholder="https://drive.google.com/file/d/..."
                  value={presentacion.imagen_url}
                  onChange={e => setPresentacion(p => ({ ...p, imagen_url: e.target.value }))}
                />
              </Col>
              <Col>
                <Upload accept="image/*,.pdf" showUploadList={false} beforeUpload={handleUploadPresentacion}>
                  <Tooltip title="Subir imagen o PDF (hasta 60 MB)">
                    <Button icon={<UploadOutlined />} />
                  </Tooltip>
                </Upload>
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {/* ── Vista: por categoría y raza, o por corral (sólo el catálogo) ── */}
      {eligeEstructura(modo) && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Cómo querés ordenar el contenido?</Text>
          <Radio.Group
            value={vistaContenido}
            onChange={e => handleSelectVista(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            {Object.entries(VISTA_CONTENIDO_INFO).map(([clave, info]) => (
              <Radio.Button value={clave} key={clave}>{info.label}</Radio.Button>
            ))}
          </Radio.Group>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {VISTA_CONTENIDO_INFO[vistaContenido].desc}
          </Text>
        </Card>
      )}

      {/* ── Lista de secciones ── */}
      {loadingSecciones ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin /> <Text style={{ marginLeft: 8 }}>Cargando secciones...</Text>
        </div>
      ) : (
        <>
          {secciones.map((sec, idx) => {
            const grupoKey = claveGrupos(sec);
            return (
              <SeccionItem
                key={sec._uid}
                seccion={sec}
                idx={idx}
                total={secciones.length}
                onMover={mover}
                onMoverA={moverA}
                onEliminar={eliminar}
                onActualizar={actualizarSeccion}
                onUpload={handleUploadImagen}
                eventoId={eventoId}
                grupos={tieneAnimales(sec) ? (grupos[grupoKey] || []) : []}
                onCrearGrupo={(animalIds) => crearGrupo(grupoKey, animalIds)}
                onEditarGrupo={(grupoId, animalIds) => editarGrupo(grupoKey, grupoId, animalIds)}
                onEliminarGrupo={(grupoId) => eliminarGrupo(grupoKey, grupoId)}
              />
            );
          })}

          <Space style={{ marginTop: 12, width: '100%' }} styles={{ item: { flex: 1 } }}>
            {/* La portada suelta es de la vista por corral: en la otra cada
                grupo ya trae la suya. */}
            {vistaContenido === 'corral' && (
              <Button type="dashed" icon={<BookOutlined />} block onClick={agregarPortada}>
                Agregar portada
              </Button>
            )}
            <Button type="dashed" icon={<PlusOutlined />} block onClick={agregarPublicidad}>
              Agregar publicidad
            </Button>
          </Space>
        </>
      )}
    </div>
  );

  // Sin `maxWidth: 600`: el resumen de carátulas y tablas tiene hasta cinco
  // datos y en 600px las columnas se pisaban. Va al ancho del asistente,
  // como la barra de pasos; el botón de generar queda debajo igual.
  const step2 = (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Resumen y generación</Title>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Text type="secondary">Evento</Text>
            <br />
            <Text strong>{eventos.find(e => e.id_evento === eventoId)?.nombre_evento || '—'}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">Formato</Text>
            <br />
            <Tag color={MODO_INFO[modo]?.tag} style={{ ...TAG_RESUMEN, fontSize: 13 }}>
              {MODO_INFO[modo]?.label}
            </Tag>
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        {/* Las carátulas son carteles sueltos por corral: no llevan portada ni
            secciones, así que el resumen no debe decir que las incluye. */}
        {tieneEstructura(modo) && (
          <Row gutter={[16, 12]} style={{ marginBottom: 12 }}>
            {eligeEstructura(modo) && (
              <Col xs={24} sm={8}>
                <Text type="secondary">Estructura</Text>
                <br />
                <Tag color="purple" style={TAG_RESUMEN}>{ESTRUCTURA_INFO[estructura].label}</Tag>
              </Col>
            )}
            {eligeGenealogia && (
              <Col xs={24} sm={8}>
                <Text type="secondary">Genealogía</Text>
                <br />
                <Tag color="purple" style={TAG_RESUMEN}>{infoGenealogia[genealogiaElegida].label}</Tag>
              </Col>
            )}
            <Col xs={24} sm={8}>
              <Text type="secondary">DEP's</Text>
              <br />
              <Tag color="purple" style={TAG_RESUMEN}>{DEPS_VISTA_INFO[depsVista].label}</Tag>
            </Col>
          </Row>
        )}
        {modo === 'tablas' && (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12}>
              <Text type="secondary">Orden</Text>
              <br />
              <Tag color="gold" style={TAG_RESUMEN}>
                {ordenTablas === 'corral' ? 'Por corral' : 'Por categoría'}
              </Tag>
            </Col>
          </Row>
        )}

        {modo === 'caratulas' ? (
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={8}>
              <Text type="secondary">Agrupación</Text>
              <br />
              <Tag color="gold" style={TAG_RESUMEN}>
                {granularidad === 'corral' ? 'Una por corral' : 'Como se muestra en el evento'}
              </Tag>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Logo</Text>
              <br />
              <Tag color="gold" style={TAG_RESUMEN}>
                {resumenCabecera}
              </Tag>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Portada y secciones</Text>
              <br />
              <Text type="secondary">No aplican a este formato</Text>
            </Col>
          </Row>
        ) : (
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={8}>
              <Text type="secondary">Cabecera</Text>
              <br />
              <Tag color="gold" style={TAG_RESUMEN}>
                {resumenCabecera}
              </Tag>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Portada</Text>
              <br />
              <Tag color={presentacion.activa ? 'green' : 'default'} style={TAG_RESUMEN}>
                {presentacion.activa ? 'Incluida' : 'Sin portada'}
              </Tag>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Secciones activas</Text>
              <br />
              <Text strong>{secciones.filter(s => s.activa).length}</Text>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        {modo !== 'caratulas' && secciones.filter(s => s.activa).map(s => (
          <Tag
            key={s._uid}
            icon={ICONO_SECCION[s.tipo] || <FileImageOutlined />}
            color={COLOR_SECCION[s.tipo] || 'blue'}
            style={{ marginBottom: 6 }}
          >
            {s.titulo || (s.tipo === 'portada' ? 'Portada' : 'Publicidad')}
            {tieneAnimales(s) && s.total_animales > 0 && ` (${s.total_animales})`}
          </Tag>
        ))}
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
        message={MODO_INFO[modo]?.alerta}
      />

      {/* La maquetación se guarda sola al generar; el botón es para guardar
          SIN generar (dejar el armado a medias y seguir otro día). */}
      <Button
        icon={<SaveOutlined />}
        loading={guardandoMaq}
        onClick={() => guardarMaquetacion(false)}
        block
        style={{ marginBottom: 12 }}
      >
        {maquetacion
          ? `Guardar maquetación (vale hasta el ${fechaCorta(maquetacion.vence_en)})`
          : 'Guardar maquetación (se conserva 7 días)'}
      </Button>
      {eligeEstructura(modo) ? (
        // El catálogo se genera para imprenta O digital desde la MISMA
        // maquetación. Un botón por salida; no hay «generar las dos»: el
        // backend genera de a un catálogo por vez y son dos descargas.
        <Row gutter={[12, 12]}>
          {Object.entries(SALIDA_INFO).map(([clave, info]) => {
            const Icono = info.icono;
            const activa = salidaGenerando === clave;
            return (
              <Col xs={24} md={12} key={clave}>
                <Card size="small" style={{ height: '100%' }}>
                  <Text strong style={{ display: 'block', marginBottom: 4 }}>
                    <Icono style={{ marginRight: 6 }} />{info.titulo}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12, minHeight: 36 }}>
                    {info.desc}
                  </Text>
                  <Button
                    type="primary"
                    size="large"
                    icon={activa ? <LoadingOutlined /> : <DownloadOutlined />}
                    loading={activa}
                    disabled={generando && !activa}
                    onClick={() => handleGenerar(clave)}
                    block
                    style={{ height: 48, fontSize: 16 }}
                  >
                    {activa ? 'Generando PDF...' : info.boton}
                  </Button>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Button
          type="primary"
          size="large"
          icon={generando ? <LoadingOutlined /> : <DownloadOutlined />}
          loading={generando}
          onClick={() => handleGenerar()}
          block
          style={{ height: 48, fontSize: 16 }}
        >
          {generando ? 'Generando PDF...' : 'Generar y Descargar PDF'}
        </Button>
      )}
    </div>
  );

  // Los pasos salen de `pasosDe(modo)`: una sola lista para los Steps, para el
  // contenido y para la navegación. `stepActual` acota el índice al frame en el
  // que `modo` ya cambió y el efecto que lo resetea todavía no corrió.
  const pasos = pasosDe(modo);
  const contenidoPaso = {
    evento: step0, estructura: stepEstructura, cabecera: stepCabecera, contenido: step1, generar: step2,
  };
  const stepActual = Math.min(step, pasos.length - 1);

  return (
    <div>
      <Title level={2} style={{ marginBottom: 4 }}>
        <FilePdfOutlined /> Generar Catálogo PDF
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Generá el catálogo para imprenta o digital, las tablas por corral o las carátulas con el contenido y orden que necesitás.
      </Text>

      <Card>
        <Steps
          current={stepActual}
          items={pasos.map(k => ({ title: TITULO_PASO[k] }))}
          style={{ marginBottom: 32 }}
        />

        <div style={{ minHeight: 400 }}>
          {contenidoPaso[pasos[stepActual]]}
        </div>

        <Divider />
        <Row justify="space-between">
          <Col>
            {stepActual > 0 && (
              <Button onClick={() => setStep(stepActual - 1)}>Anterior</Button>
            )}
          </Col>
          <Col>
            {stepActual < pasos.length - 1 && (
              <Button
                type="primary"
                onClick={() => setStep(stepActual + 1)}
                disabled={stepActual === 0 && !eventoId}
              >
                Siguiente
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Modal informativo durante la generacion del PDF (2-3 min).
          La request sigue siendo sincrona (responseType: blob; el timeout
          vive en api.js y debe quedar por encima de lo que promete este
          modal). Le da contexto al usuario y le pide que no cierre la
          pestania. */}
      <Modal
        open={generando}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={460}
        centered
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin
            size="large"
            indicator={<LoadingOutlined style={{ fontSize: 40, color: '#1890ff' }} spin />}
          />
          <Title level={4} style={{ marginTop: 20, marginBottom: 6 }}>
            Generando tu catálogo PDF
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
            Esto puede tardar entre 2 y 3 minutos, según la cantidad de
            animales e imágenes del evento.
          </Text>
          <Text strong style={{ display: 'block', marginBottom: 18, fontSize: 18, color: '#1890ff' }}>
            ⏱ {formatTiempo(tiempoTranscurrido)}
          </Text>
          <Alert
            type="warning"
            showIcon
            message="No cierres ni recargues esta pestaña"
            description="Si la cerrás, vas a cancelar la generación y vas a tener que empezar de nuevo. Podés cambiar de pestaña del navegador y volver más tarde."
            style={{ textAlign: 'left' }}
          />
        </div>
      </Modal>
    </div>
  );
};


// Ícono y color por tipo de sección: los usan el ítem y el resumen final.
const ICONO_SECCION = {
  grupo: <TeamOutlined />,
  corral: <AppstoreOutlined />,
  condiciones: <FileTextOutlined />,
  publicidad: <FileImageOutlined />,
  portada: <BookOutlined />,
};
const COLOR_SECCION = {
  grupo: 'green', corral: 'cyan', condiciones: 'orange', publicidad: 'blue', portada: 'magenta',
};
const LABEL_SECCION = {
  grupo: 'Grupo', corral: 'Corral', condiciones: 'Términos', publicidad: 'Publicidad', portada: 'Portada',
};


// ─── «Mover a la posición N» ─────────────────────────────────────────────────
// El botón dice en qué posición está el ítem; al tocarlo se escribe a cuál va.
const MoverAPosicion = ({ idx, total, onMoverA }) => {
  const [abierto, setAbierto] = useState(false);
  const [destino, setDestino] = useState(idx + 1);
  const ir = () => {
    if (destino) onMoverA(idx, destino - 1);
    setAbierto(false);
  };
  return (
    <Popover
      trigger="click"
      open={abierto}
      onOpenChange={(v) => { setAbierto(v); if (v) setDestino(idx + 1); }}
      content={(
        <Space>
          <InputNumber
            size="small"
            min={1}
            max={total}
            value={destino}
            onChange={setDestino}
            onPressEnter={ir}
            autoFocus
            style={{ width: 70 }}
          />
          <Button size="small" type="primary" onClick={ir}>Ir</Button>
        </Space>
      )}
      title={`Mover a la posición (1 a ${total})`}
    >
      <Tooltip title="Mover a una posición">
        <Button size="small" style={{ minWidth: 36, padding: '0 6px', fontSize: 11 }}>
          #{idx + 1}
        </Button>
      </Tooltip>
    </Popover>
  );
};


// ─── Ítem de sección ordenable ───────────────────────────────────────────────
const SeccionItem = ({
  seccion, idx, total, onMover, onMoverA, onEliminar, onActualizar, onUpload,
  eventoId, grupos, onCrearGrupo, onEditarGrupo, onEliminarGrupo,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Estado del panel de animales/agrupación
  const [animales, setAnimales] = useState([]);
  const [loadingAnimales, setLoadingAnimales] = useState(false);
  const [animalesCargados, setAnimalesCargados] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [grupoEditando, setGrupoEditando] = useState(null);

  // Cargar animales al expandir una sección con animales (grupo o corral)
  useEffect(() => {
    if (!expanded || !tieneAnimales(seccion) || animalesCargados) return;
    setLoadingAnimales(true);
    const filtro = seccion.tipo === 'corral'
      ? { corral: seccion.corral }
      : { id_categoria: seccion.id_categoria, id_raza: seccion.id_raza };
    pdfService.getAnimalesSeccion(eventoId, filtro)
      .then(({ data }) => { setAnimales(data.animales || []); setAnimalesCargados(true); })
      .catch(err => message.error(err.response?.data?.error || 'Error al cargar animales de la sección'))
      .finally(() => setLoadingAnimales(false));
  }, [expanded, seccion, animalesCargados, eventoId]);

  // IDs de animales ya en algún grupo
  const idsEnGrupos = new Set(grupos.flatMap(g => g.animalIds));
  const idsGrupoEditando = new Set(
    grupoEditando ? (grupos.find(g => g.id === grupoEditando)?.animalIds || []) : []
  );

  const toggleSeleccion = (id) => {
    setSeleccionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAgrupar = () => {
    if (seleccionados.length < 2 || seleccionados.length > 4) return;
    if (grupoEditando) {
      onEditarGrupo(grupoEditando, seleccionados);
      setGrupoEditando(null);
    } else {
      onCrearGrupo(seleccionados);
    }
    setSeleccionados([]);
  };

  const iniciarEdicion = (grupo) => {
    setGrupoEditando(grupo.id);
    setSeleccionados([...grupo.animalIds]);
  };

  const cancelarEdicion = () => {
    setGrupoEditando(null);
    setSeleccionados([]);
  };

  const tag = { color: COLOR_SECCION[seccion.tipo] || 'default', label: LABEL_SECCION[seccion.tipo] || seccion.tipo };
  const esPub = seccion.tipo === 'publicidad';
  const esGrupo = seccion.tipo === 'grupo';
  const esCorral = seccion.tipo === 'corral';
  const esPortada = seccion.tipo === 'portada';
  const esCondiciones = seccion.tipo === 'condiciones';
  // Qué lleva el panel: título e imagen de portada (grupo, publicidad y
  // portada suelta) y la agrupación de a dos (grupo y corral).
  const conPortada = esGrupo || esPub || esPortada;
  const conAnimales = esGrupo || esCorral;
  const esExpandible = conPortada || conAnimales || esCondiciones;

  return (
    <Card
      size="small"
      style={{
        marginBottom: 8,
        opacity: seccion.activa ? 1 : 0.5,
        border: seccion.activa ? '1px solid #d9d9d9' : '1px dashed #d9d9d9',
      }}
      styles={{ body: { padding: '10px 12px' } }}
    >
      <Row align="middle" gutter={8} wrap={false}>
        {/* Controles orden: flechas y «mover a la posición» */}
        <Col>
          <Space direction="vertical" size={2} align="center">
            <Button size="small" icon={<ArrowUpOutlined />} disabled={idx === 0}
              onClick={() => onMover(idx, -1)} />
            <MoverAPosicion idx={idx} total={total} onMoverA={onMoverA} />
            <Button size="small" icon={<ArrowDownOutlined />} disabled={idx === total - 1}
              onClick={() => onMover(idx, 1)} />
          </Space>
        </Col>

        {/* Ícono + título */}
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Row align="middle" gutter={8} wrap={false}>
            <Col style={{ color: '#8c8c8c' }}>{ICONO_SECCION[seccion.tipo]}</Col>
            <Col flex="auto">
              <Space size={6} align="center">
                <Text
                  strong
                  style={{ fontSize: 13, cursor: esExpandible ? 'pointer' : 'default' }}
                  onClick={() => esExpandible && setExpanded(e => !e)}
                >
                  {seccion.titulo || (esPortada ? 'Portada sin título' : 'Publicidad sin título')}
                </Text>
                {esExpandible && (
                  <Tag
                    icon={<EditOutlined />}
                    color="blue"
                    style={{ cursor: 'pointer', fontSize: 10, margin: 0 }}
                    onClick={() => setExpanded(e => !e)}
                  >
                    Editar
                  </Tag>
                )}
              </Space>
              {seccion.total_animales > 0 && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                  ({seccion.total_animales} animales)
                </Text>
              )}
            </Col>
            <Col><Tag color={tag.color} style={{ fontSize: 10 }}>{tag.label}</Tag></Col>
          </Row>
        </Col>

        {/* Toggle activo */}
        <Col>
          <Switch
            size="small"
            checked={seccion.activa}
            onChange={v => onActualizar(idx, { activa: v })}
          />
        </Col>

        {/* Eliminar (publicidades y portadas sueltas: lo que agregó el productor) */}
        {(esPub || esPortada) && (
          <Col>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onEliminar(idx)}
            />
          </Col>
        )}
      </Row>

      {/* Panel expandible — Condiciones */}
      {expanded && esCondiciones && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
          <Text style={{ fontSize: 11, display: 'block', marginBottom: 6 }} strong>Condiciones Comerciales</Text>
          <RichTextEditor
            value={seccion.condiciones_comerciales || ''}
            onChange={v => onActualizar(idx, { condiciones_comerciales: v })}
          />
          <Text style={{ fontSize: 11, display: 'block', margin: '12px 0 6px' }} strong>Condiciones Sanitarias</Text>
          <RichTextEditor
            value={seccion.condiciones_sanitarias || ''}
            onChange={v => onActualizar(idx, { condiciones_sanitarias: v })}
          />
          <Text style={{ fontSize: 11, display: 'block', margin: '12px 0 6px' }} strong>Reclamos y Garantías</Text>
          <RichTextEditor
            value={seccion.reclamos_garantias || ''}
            onChange={v => onActualizar(idx, { reclamos_garantias: v })}
          />
        </div>
      )}

      {/* Panel expandible para configurar */}
      {expanded && (conPortada || conAnimales) && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
          {/* Título editable */}
          {conPortada && (
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col span={24}>
              <Text style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Título de portada</Text>
              <Input
                size="small"
                value={seccion.titulo}
                onChange={e => onActualizar(idx, { titulo: e.target.value })}
              />
            </Col>
          </Row>
          )}

          {/* Subtítulo (solo publicidades) */}
          {esPub && (
            <>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col span={24}>
                  <Text style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Subtítulo</Text>
                  <Input
                    size="small"
                    value={seccion.subtitulo || ''}
                    onChange={e => onActualizar(idx, { subtitulo: e.target.value })}
                  />
                </Col>
              </Row>
              <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col span={24}>
                  <Text style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Tipo de página</Text>
                  <Radio.Group
                    size="small"
                    value={seccion.subtipo || 'texto'}
                    onChange={e => onActualizar(idx, { subtipo: e.target.value })}
                  >
                    <Radio.Button value="texto">Texto + imagen</Radio.Button>
                    <Radio.Button value="imagen_completa">Imagen completa</Radio.Button>
                  </Radio.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Imagen */}
          {conPortada && (
          <Row gutter={8} align="middle">
            <Col flex="auto">
              <Text style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Imagen de fondo (URL, o subí una imagen o un PDF)
              </Text>
              <Input
                size="small"
                placeholder="https://drive.google.com/file/d/..."
                value={seccion.imagen_url || ''}
                onChange={e => onActualizar(idx, { imagen_url: e.target.value })}
              />
            </Col>
            <Col style={{ marginTop: 18 }}>
              <Upload
                accept="image/*,.pdf"
                showUploadList={false}
                beforeUpload={file => onUpload(file, idx)}
              >
                <Tooltip title="Subir imagen o PDF (hasta 60 MB)">
                  <Button size="small" icon={<UploadOutlined />} />
                </Tooltip>
              </Upload>
            </Col>
          </Row>
          )}

          {/* Panel de agrupación — secciones con animales (grupo y corral) */}
          {conAnimales && (
            <div style={conPortada ? { marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0' } : undefined}>
              <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Agrupación de animales
              </Text>

              {loadingAnimales ? (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin size="small" />
                </div>
              ) : (
                <>
                  {/* Lista de animales */}
                  <div style={{
                    maxHeight: 280, overflowY: 'auto',
                    border: '1px solid #f0f0f0', borderRadius: 6,
                    padding: '4px 8px', marginBottom: 10,
                  }}>
                    {animales.length === 0 ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>Sin animales en esta sección.</Text>
                    ) : animales.map(a => {
                      const enOtroGrupo = idsEnGrupos.has(a.id_animal) && !idsGrupoEditando.has(a.id_animal);
                      const grupoPertenece = !grupoEditando && grupos.find(g => g.animalIds.includes(a.id_animal));
                      return (
                        <div
                          key={a.id_animal}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '5px 0', borderBottom: '1px solid #fafafa',
                            opacity: enOtroGrupo ? 0.4 : 1,
                          }}
                        >
                          <Checkbox
                            checked={seleccionados.includes(a.id_animal)}
                            disabled={enOtroGrupo}
                            onChange={() => toggleSeleccion(a.id_animal)}
                          />
                          <Text style={{ fontSize: 12, flex: 1 }}>{a.nom_animal}</Text>
                          <Tag style={{ fontSize: 10, margin: 0 }}>
                            Corral {a.corral}{a.es_corral_compartido ? ' ⬡' : ''}
                          </Tag>
                          {!a.tiene_foto_principal && (
                            <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>sin foto</Tag>
                          )}
                          {grupoPertenece && (
                            <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>{grupoPertenece.label}</Tag>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Acciones */}
                  <Space style={{ marginBottom: 12 }}>
                    <Button
                      size="small"
                      type="primary"
                      disabled={seleccionados.length < 2 || seleccionados.length > 4}
                      onClick={handleAgrupar}
                    >
                      {grupoEditando
                        ? `Guardar grupo (${seleccionados.length})`
                        : `Agrupar ${seleccionados.length} seleccionados`}
                    </Button>
                    {grupoEditando && (
                      <Button size="small" onClick={cancelarEdicion}>Cancelar</Button>
                    )}
                  </Space>

                  {/* Grupos creados */}
                  {grupos.length > 0 && (
                    <div>
                      <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 6 }}>
                        Grupos armados
                      </Text>
                      {grupos.map(g => {
                        const nombres = g.animalIds
                          .map(id => animales.find(a => a.id_animal === id)?.nom_animal || `#${id}`)
                          .join(', ');
                        return (
                          <div
                            key={g.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '5px 8px', background: '#f9f0ff',
                              borderRadius: 4, marginBottom: 4,
                            }}
                          >
                            <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>{g.label}</Tag>
                            <Text style={{ fontSize: 11, flex: 1 }} ellipsis={{ tooltip: nombres }}>{nombres}</Text>
                            <Button
                              size="small" type="link" style={{ padding: '0 4px', fontSize: 11 }}
                              disabled={!!grupoEditando}
                              onClick={() => iniciarEdicion(g)}
                            >
                              Editar
                            </Button>
                            <Button
                              size="small" type="link" danger style={{ padding: '0 4px', fontSize: 11 }}
                              disabled={!!grupoEditando && grupoEditando !== g.id}
                              onClick={() => {
                                onEliminarGrupo(g.id);
                                if (grupoEditando === g.id) cancelarEdicion();
                              }}
                            >
                              Quitar
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};


export default GenerarPDF;
